/**
 * A tela de administração de um CATÁLOGO do SGQ.
 *
 * POR QUE UM COMPONENTE E NÃO DUAS TELAS.
 *
 * Tipo de reunião e tipo de solicitação são a mesma coisa com flags diferentes:
 * nome, código, ordem, ativação, contagem de uso, tipo do sistema. Escrever duas
 * telas criaria duas verdades sobre o mesmo comportamento — e a segunda a ser
 * corrigida seria sempre a esquecida. O que varia entre as duas é declarado
 * como dados, em `flags`.
 *
 * AS TRÊS REGRAS QUE ESTA TELA PRECISA TORNAR ÓBVIAS, e que o backend impõe:
 *
 *  1. O COMPORTAMENTO VEM DAS FLAGS, NUNCA DO NOME. O legado descobria se uma
 *     reunião era SACAP com `LIKE '%sacap%'` sobre o nome do tipo, e renomear
 *     apagava a exigência regulatória em silêncio (BUG-REU-02). Aqui o nome é
 *     rótulo, e a regra é caixa de seleção — a tela mostra as duas coisas
 *     separadas para que ninguém volte a confundi-las.
 *
 *  2. TIPO DO SISTEMA SE DESATIVA, NÃO SE EXCLUI. Excluir apagaria a referência
 *     de uma ata assinada, que é registro §11.10(e). Desativar tira o tipo dos
 *     formulários novos e não toca em nada do que já existe. A tela oferece a
 *     saída em vez de deixar o usuário descobrir pelo erro.
 *
 *  3. USO É AVISO PRÉVIO, NÃO SURPRESA. A contagem vem na listagem, então o
 *     aviso aparece antes da tentativa — tentar para aprender é o que faz o
 *     usuário perder confiança na tela.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Dialog,
  EmptyState,
  Field,
  FormError,
  Input,
  Switch,
  Textarea,
  useToast,
} from '@/design-system';
import { toUserMessage } from '@/lib/api';
import { PageBody, PageHeader } from '@/shell/app-shell';

/** Uma regra de comportamento do tipo, declarada pela tela que usa o componente. */
export interface SupportTypeFlag<T> {
  readonly key: keyof T & string;
  /** Como a pessoa lê a regra. Nunca o nome do campo. */
  readonly label: string;
  /** O que a regra causa, em uma frase. */
  readonly description: string;
}

export interface SupportTypeRecord {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly sortOrder: number;
  readonly active: boolean;
  readonly system: boolean;
  readonly usageCount: number;
}

export interface SupportTypeAdminProps<T extends SupportTypeRecord> {
  readonly title: string;
  readonly description: string;
  /** Singular, em minúsculas: "tipo de reunião". Entra nas frases da tela. */
  readonly noun: string;
  /** Como o registro que usa o tipo se chama: "reunião", "solicitação". */
  readonly usageNoun: string;
  readonly breadcrumbLabel: string;
  readonly queryKey: readonly unknown[];
  readonly flags: readonly SupportTypeFlag<T>[];
  readonly api: {
    list: (params?: { includeInactive?: boolean }) => Promise<T[]>;
    create: (body: Record<string, unknown>) => Promise<T>;
    update: (id: string, body: Record<string, unknown>) => Promise<T>;
    remove: (id: string) => Promise<void>;
  };
}

const CODIGO_VALIDO = /^[A-Z][A-Z0-9_]{1,31}$/;

interface Rascunho {
  code: string;
  name: string;
  description: string;
  sortOrder: string;
  active: boolean;
  flags: Record<string, boolean>;
}

export function SupportTypeAdmin<T extends SupportTypeRecord>({
  title,
  description,
  noun,
  usageNoun,
  breadcrumbLabel,
  queryKey,
  flags,
  api,
}: SupportTypeAdminProps<T>) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [editando, setEditando] = useState<T | null>(null);
  const [criando, setCriando] = useState(false);
  const [excluindo, setExcluindo] = useState<T | null>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [errosCampo, setErrosCampo] = useState<Record<string, string>>({});
  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoVazio(flags));

  /*
   * `includeInactive` é sempre verdadeiro AQUI. Sem isso, desativar um tipo o
   * faria sumir da própria tela onde se desativa, e não haveria como reativá-lo
   * — o usuário ficaria com um tipo invisível e permanente. Os formulários de
   * reunião e de solicitação chamam sem a flag e recebem só os ativos.
   */
  const listaQuery = useQuery({
    queryKey,
    queryFn: () => api.list({ includeInactive: true }),
  });

  const tipos = useMemo(
    () => [...(listaQuery.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [listaQuery.data],
  );

  async function recarregar() {
    await queryClient.invalidateQueries({ queryKey });
  }

  const salvar = useMutation({
    mutationFn: async () => {
      const corpo: Record<string, unknown> = {
        name: rascunho.name.trim(),
        description: rascunho.description.trim(),
        sortOrder: Number(rascunho.sortOrder || '0'),
        active: rascunho.active,
        ...rascunho.flags,
      };
      if (editando) return api.update(editando.id, corpo);
      return api.create({ ...corpo, code: rascunho.code.trim().toUpperCase() });
    },
    onSuccess: async () => {
      await recarregar();
      toast({
        tone: 'success',
        title: editando ? `${maiuscula(noun)} atualizado` : `${maiuscula(noun)} criado`,
      });
      fecharFormulario();
    },
    onError: (error) => setErroEnvio(toUserMessage(error)),
  });

  const alternarAtivo = useMutation({
    mutationFn: ({ tipo, active }: { tipo: T; active: boolean }) =>
      api.update(tipo.id, { active }),
    onSuccess: async (_data, variables) => {
      await recarregar();
      toast({
        tone: 'success',
        title: variables.active ? `${maiuscula(noun)} reativado` : `${maiuscula(noun)} desativado`,
        description: variables.active
          ? `Volta a aparecer na escolha de ${usageNoun} nova.`
          : `Deixa de aparecer na escolha de ${usageNoun} nova. Os registros existentes não mudam.`,
      });
    },
    onError: (error) => toast({ tone: 'error', title: toUserMessage(error) }),
  });

  const excluir = useMutation({
    mutationFn: (tipo: T) => api.remove(tipo.id),
    onSuccess: async () => {
      await recarregar();
      toast({ tone: 'success', title: `${maiuscula(noun)} excluído` });
      setExcluindo(null);
    },
    onError: (error) => {
      toast({ tone: 'error', title: toUserMessage(error) });
      setExcluindo(null);
    },
  });

  function abrirCriacao() {
    setEditando(null);
    setRascunho(rascunhoVazio(flags));
    setErrosCampo({});
    setErroEnvio(null);
    setCriando(true);
  }

  function abrirEdicao(tipo: T) {
    setEditando(tipo);
    setRascunho({
      code: tipo.code,
      name: tipo.name,
      description: tipo.description,
      sortOrder: String(tipo.sortOrder),
      active: tipo.active,
      flags: Object.fromEntries(
        flags.map((flag) => [flag.key, Boolean((tipo as unknown as Record<string, unknown>)[flag.key])]),
      ),
    });
    setErrosCampo({});
    setErroEnvio(null);
    setCriando(true);
  }

  function fecharFormulario() {
    setCriando(false);
    setEditando(null);
    setErrosCampo({});
    setErroEnvio(null);
  }

  function validar(): Record<string, string> {
    const erros: Record<string, string> = {};
    if (rascunho.name.trim() === '') erros.name = `Informe o nome do ${noun}.`;
    if (!editando) {
      const code = rascunho.code.trim().toUpperCase();
      if (code === '') erros.code = 'Informe o código.';
      else if (!CODIGO_VALIDO.test(code)) {
        erros.code =
          'Use letras maiúsculas, números e sublinhado, começando por letra. Exemplo: ANALISE_CRITICA.';
      }
    }
    return erros;
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: breadcrumbLabel }]}
        title={title}
        description={description}
        actions={
          <Button variant="primary" onClick={abrirCriacao}>
            Novo {noun}
          </Button>
        }
      />

      <PageBody>
        {listaQuery.isPending ? (
          <Card>
            <p className="text-sm text-content-muted">Carregando…</p>
          </Card>
        ) : listaQuery.isError ? (
          <FormError title="Não foi possível carregar a lista">
            {toUserMessage(listaQuery.error)}
          </FormError>
        ) : tipos.length === 0 ? (
          <EmptyState
            title={`Nenhum ${noun} cadastrado`}
            description={`Sem ${noun} não há como registrar ${usageNoun}.`}
            action={
              <Button variant="primary" onClick={abrirCriacao}>
                Criar o primeiro {noun}
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {tipos.map((tipo) => (
              <li key={tipo.id}>
                <Card className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-medium text-content">{tipo.name}</h3>
                        <Badge tone="neutral">{tipo.code}</Badge>
                        {tipo.system ? <Badge tone="info">Do sistema</Badge> : null}
                        {tipo.active ? null : <Badge tone="warning">Desativado</Badge>}
                      </div>
                      {tipo.description ? (
                        <p className="mt-1 text-sm text-content-muted">{tipo.description}</p>
                      ) : null}
                    </div>

                    <ButtonGroup>
                      <Button variant="ghost" size="sm" onClick={() => abrirEdicao(tipo)}>
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={alternarAtivo.isPending}
                        onClick={() =>
                          alternarAtivo.mutate({ tipo, active: !tipo.active })
                        }
                      >
                        {tipo.active ? 'Desativar' : 'Reativar'}
                      </Button>
                      {/*
                       * Tipo do sistema não tem botão de excluir. Mostrar o botão
                       * para depois recusar seria ensinar o usuário a tentar; o
                       * caminho é explicado logo abaixo, onde ele olha.
                       */}
                      {tipo.system ? null : (
                        <Button variant="ghost" size="sm" onClick={() => setExcluindo(tipo)}>
                          Excluir
                        </Button>
                      )}
                    </ButtonGroup>
                  </div>

                  <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    {flags.map((flag) => (
                      <div key={flag.key} className="flex items-center gap-1.5">
                        <dt className="text-content-muted">{flag.label}:</dt>
                        <dd className="font-medium text-content">
                          {(tipo as unknown as Record<string, unknown>)[flag.key] ? 'sim' : 'não'}
                        </dd>
                      </div>
                    ))}
                    <div className="flex items-center gap-1.5">
                      <dt className="text-content-muted">Em uso por:</dt>
                      <dd className="font-medium text-content">
                        {tipo.usageCount} {tipo.usageCount === 1 ? usageNoun : `${usageNoun}s`}
                      </dd>
                    </div>
                  </dl>

                  {tipo.system ? (
                    <p className="text-xs text-content-subtle">
                      Tipo do sistema: pode ser renomeado e desativado, mas não excluído — há
                      registros assinados que o citam pelo código.
                    </p>
                  ) : null}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </PageBody>

      <Dialog
        open={criando}
        onOpenChange={(aberto) => (aberto ? undefined : fecharFormulario())}
        title={editando ? `Editar ${noun}` : `Novo ${noun}`}
        description={
          editando
            ? 'O código não muda: é por ele que os registros já emitidos se referem a este tipo.'
            : 'O código identifica o tipo nos registros e não pode ser alterado depois.'
        }
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setErroEnvio(null);
            const erros = validar();
            setErrosCampo(erros);
            if (Object.keys(erros).length > 0) {
              document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
              return;
            }
            salvar.mutate();
          }}
        >
          {erroEnvio ? <FormError title="Não foi possível salvar">{erroEnvio}</FormError> : null}

          <Field label="Nome" required error={errosCampo.name}>
            <Input
              value={rascunho.name}
              onChange={(event) => setRascunho((r) => ({ ...r, name: event.target.value }))}
              maxLength={255}
              autoFocus
            />
          </Field>

          <Field
            label="Código"
            required={!editando}
            error={errosCampo.code}
            description={
              editando
                ? 'Imutável — registros assinados se referem a este código.'
                : 'Maiúsculas, números e sublinhado. Exemplo: ANALISE_CRITICA.'
            }
          >
            <Input
              value={rascunho.code}
              onChange={(event) =>
                setRascunho((r) => ({ ...r, code: event.target.value.toUpperCase() }))
              }
              maxLength={32}
              readOnly={Boolean(editando)}
              disabled={Boolean(editando)}
            />
          </Field>

          <Field label="Descrição" description="Aparece como ajuda para quem escolher o tipo.">
            <Textarea
              value={rascunho.description}
              onChange={(event) => setRascunho((r) => ({ ...r, description: event.target.value }))}
              maxLength={4000}
              rows={3}
            />
          </Field>

          <Field label="Ordem na lista" description="Menor aparece primeiro.">
            <Input
              type="number"
              min={0}
              max={9999}
              value={rascunho.sortOrder}
              onChange={(event) => setRascunho((r) => ({ ...r, sortOrder: event.target.value }))}
            />
          </Field>

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-1 text-sm font-medium text-content">
              O que este {noun} exige
            </legend>
            {/*
             * As regras vivem AQUI, em caixas de seleção, e não no nome. É a
             * diferença entre um catálogo de rótulos e um catálogo de
             * comportamento declarado — e é o que impede que renomear um tipo
             * mude, sem querer, uma exigência regulatória.
             */}
            {flags.map((flag) => (
              <Switch
                key={flag.key}
                checked={Boolean(rascunho.flags[flag.key])}
                onCheckedChange={(marcado) =>
                  setRascunho((r) => ({ ...r, flags: { ...r.flags, [flag.key]: marcado } }))
                }
                label={flag.label}
                description={flag.description}
              />
            ))}
          </fieldset>

          <Switch
            checked={rascunho.active}
            onCheckedChange={(marcado) => setRascunho((r) => ({ ...r, active: marcado }))}
            label="Ativo"
            description={`Desativado, some da escolha de ${usageNoun} nova sem alterar os registros existentes.`}
          />

          <ButtonGroup className="justify-end">
            <Button type="button" variant="secondary" onClick={fecharFormulario}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={salvar.isPending}>
              {editando ? 'Salvar alterações' : `Criar ${noun}`}
            </Button>
          </ButtonGroup>
        </form>
      </Dialog>

      <Dialog
        open={excluindo !== null}
        onOpenChange={(aberto) => (aberto ? undefined : setExcluindo(null))}
        title={`Excluir ${noun}?`}
        description={
          excluindo && excluindo.usageCount > 0
            ? `Este ${noun} está em uso por ${excluindo.usageCount} ${
                excluindo.usageCount === 1 ? usageNoun : `${usageNoun}s`
              }. A exclusão será recusada — desativar é o que resolve.`
            : 'A exclusão é definitiva.'
        }
      >
        <div className="flex flex-col gap-4">
          {excluindo && excluindo.usageCount > 0 ? (
            <p className="text-sm text-content-muted">
              Desativar tira o {noun} da escolha de {usageNoun} nova e mantém intactos os
              registros que já o citam — inclusive os assinados.
            </p>
          ) : null}

          <ButtonGroup className="justify-end">
            <Button type="button" variant="secondary" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            {excluindo && excluindo.usageCount > 0 ? (
              <Button
                type="button"
                variant="primary"
                loading={alternarAtivo.isPending}
                onClick={() => {
                  alternarAtivo.mutate({ tipo: excluindo, active: false });
                  setExcluindo(null);
                }}
              >
                Desativar em vez de excluir
              </Button>
            ) : (
              <Button
                type="button"
                variant="danger"
                loading={excluir.isPending}
                onClick={() => excluindo && excluir.mutate(excluindo)}
              >
                Excluir {noun}
              </Button>
            )}
          </ButtonGroup>
        </div>
      </Dialog>
    </>
  );
}

function rascunhoVazio<T>(flags: readonly SupportTypeFlag<T>[]): Rascunho {
  return {
    code: '',
    name: '',
    description: '',
    sortOrder: '0',
    active: true,
    flags: Object.fromEntries(flags.map((flag) => [flag.key, false])),
  };
}

function maiuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}
